/**
 * Sign-in for the static SPA, talking to Amazon Cognito's public API directly
 * (no SDK dependency). Configured at runtime from /config.js:
 *
 *   window.__SATI_CONFIG__.auth = { region, clientId, loginRequired }
 *
 * No `auth` block -> sign-in is disabled and the app behaves as before.
 *
 * Tokens: the ID token (1h) is sent to the AI gateway as
 * `Authorization: Bearer <idToken>`; the refresh token (7 days, revocable)
 * renews it silently. They are kept in localStorage so a salesperson is not
 * signed out on every tab close. Nothing here is a secret of the app itself:
 * the client id is public by design, and the user's password is sent only to
 * Cognito over TLS.
 */

export type AuthConfig = { region: string; clientId: string; loginRequired?: boolean };

type Session = { idToken: string; refreshToken: string; expiresAt: number; email: string };

const STORAGE_KEY = "pnai.session";
const listeners = new Set<() => void>();

export function getAuthConfig(): AuthConfig | null {
  if (typeof window === "undefined") return null;
  const auth = window.__SATI_CONFIG__?.auth;
  return auth?.region && auth?.clientId ? auth : null;
}

export class AuthError extends Error {
  constructor(public code: string, message: string) {
    super(message);
  }
}

async function cognito<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const cfg = getAuthConfig();
  if (!cfg) throw new AuthError("NotConfigured", "Sign-in is not configured.");
  const response = await fetch(`https://cognito-idp.${cfg.region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify({ ClientId: cfg.clientId, ...body }),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const code = String(data.__type ?? "Error").split("#").pop() ?? "Error";
    throw new AuthError(code, String(data.message ?? "Sign-in failed. Please try again."));
  }
  return data as T;
}

function readSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function writeSession(session: Session | null) {
  if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else window.localStorage.removeItem(STORAGE_KEY);
  listeners.forEach((fn) => fn());
}

function emailFromIdToken(idToken: string): string {
  try {
    const part = idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(decodeURIComponent(escape(atob(part)))) as { email?: string };
    return payload.email ?? "";
  } catch {
    return "";
  }
}

type AuthResultBody = { IdToken: string; RefreshToken?: string; ExpiresIn: number };
type InitiateResponse = { AuthenticationResult?: AuthResultBody; ChallengeName?: string; Session?: string };

function storeResult(result: AuthResultBody, refreshToken?: string) {
  const session: Session = {
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? refreshToken ?? "",
    expiresAt: Date.now() + result.ExpiresIn * 1000,
    email: emailFromIdToken(result.IdToken),
  };
  writeSession(session);
}

export type SignInOutcome = { status: "signedIn" } | { status: "newPasswordRequired"; session: string };

export async function signIn(email: string, password: string): Promise<SignInOutcome> {
  const res = await cognito<InitiateResponse>("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    AuthParameters: { USERNAME: email.trim().toLowerCase(), PASSWORD: password },
  });
  if (res.AuthenticationResult) {
    storeResult(res.AuthenticationResult);
    return { status: "signedIn" };
  }
  if (res.ChallengeName === "NEW_PASSWORD_REQUIRED" && res.Session) {
    return { status: "newPasswordRequired", session: res.Session };
  }
  throw new AuthError("UnsupportedChallenge", "This account needs a step the site does not support yet. Contact an admin.");
}

/** Invited users set their own password on first sign-in. */
export async function completeNewPassword(email: string, newPassword: string, session: string): Promise<void> {
  const res = await cognito<InitiateResponse>("RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    Session: session,
    ChallengeResponses: { USERNAME: email.trim().toLowerCase(), NEW_PASSWORD: newPassword },
  });
  if (!res.AuthenticationResult) throw new AuthError("Error", "Could not set the new password.");
  storeResult(res.AuthenticationResult);
}

export async function signUp(email: string, password: string): Promise<void> {
  const username = email.trim().toLowerCase();
  await cognito("SignUp", {
    Username: username,
    Password: password,
    UserAttributes: [{ Name: "email", Value: username }],
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await cognito("ConfirmSignUp", { Username: email.trim().toLowerCase(), ConfirmationCode: code.trim() });
}

export async function resendCode(email: string): Promise<void> {
  await cognito("ResendConfirmationCode", { Username: email.trim().toLowerCase() });
}

export async function forgotPassword(email: string): Promise<void> {
  await cognito("ForgotPassword", { Username: email.trim().toLowerCase() });
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  await cognito("ConfirmForgotPassword", {
    Username: email.trim().toLowerCase(),
    ConfirmationCode: code.trim(),
    Password: newPassword,
  });
}

export async function signOut(): Promise<void> {
  const session = readSession();
  writeSession(null);
  if (session?.refreshToken) {
    // Revoke server-side too, so a copied refresh token stops working.
    await cognito("RevokeToken", { Token: session.refreshToken }).catch(() => undefined);
  }
}

let refreshing: Promise<string | null> | null = null;

/**
 * A valid ID token for the AI gateway, refreshed when it is within 2 minutes
 * of expiry. Null when nobody is signed in (or the refresh was rejected).
 */
export async function getIdToken(): Promise<string | null> {
  if (!getAuthConfig()) return null;
  const session = readSession();
  if (!session) return null;
  if (session.expiresAt - Date.now() > 120_000) return session.idToken;
  if (!session.refreshToken) {
    writeSession(null);
    return null;
  }
  refreshing ??= (async () => {
    try {
      const res = await cognito<InitiateResponse>("InitiateAuth", {
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: { REFRESH_TOKEN: session.refreshToken },
      });
      if (!res.AuthenticationResult) throw new Error("no result");
      storeResult(res.AuthenticationResult, session.refreshToken);
      return res.AuthenticationResult.IdToken;
    } catch {
      writeSession(null);
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Headers to add to AI gateway calls: a Bearer token when signed in. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function currentUserEmail(): string | null {
  if (typeof window === "undefined") return null;
  return readSession()?.email ?? null;
}

export function onAuthChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
